import React, { useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { ColumnDef } from "@tanstack/react-table";
import TableContainer from "components/common/TableContainer";
import StatusBadge from "components/common/StatusBadge";
import { getWorkAssignments } from "features/WorkAssignments/services/WorkAssignmentService";
import type { WorkAssignment } from "features/WorkAssignments/types/work-assignment.types";
import {
  WorkAssignmentSourceTypeLabel,
  workAssignmentStatusMap,
} from "features/WorkAssignments/constants/work-assignment.constants";
import { useProfile } from "features/Authentication/hooks/useProfile";
import {
  SourceColumnContent,
  resolveMappedSourceCell,
} from "components/common/columns";
import moment from "moment";

type RangeKey = "today" | "week" | "month" | "all";

function getRange(key: RangeKey): { from: string; to: string } {
  const now = moment();
  switch (key) {
    case "today":
      return { from: now.format("YYYY-MM-DD"), to: now.format("YYYY-MM-DD") };
    case "week":
      return {
        from: now.clone().startOf("isoWeek").format("YYYY-MM-DD"),
        to: now.clone().endOf("isoWeek").format("YYYY-MM-DD"),
      };
    case "month":
      return {
        from: now.clone().startOf("month").format("YYYY-MM-DD"),
        to: now.clone().endOf("month").format("YYYY-MM-DD"),
      };
    case "all":
      return { from: "", to: "" };
  }
}

function relativePlannedStart(
  iso?: string | null,
): { label: string; cls: string } | null {
  if (!iso) return null;
  const m = moment(iso);
  if (!m.isValid()) return null;
  const diffDays = m.startOf("day").diff(moment().startOf("day"), "days");
  if (diffDays === 0)
    return { label: "Today", cls: "text-warning fw-semibold" };
  if (diffDays === 1) return { label: "Tomorrow", cls: "text-body" };
  if (diffDays === -1) return { label: "Yesterday", cls: "text-danger" };
  if (diffDays > 1) return { label: `In ${diffDays} days`, cls: "text-body" };
  return { label: `${Math.abs(diffDays)} days ago`, cls: "text-danger" };
}

const RANGE_OPTIONS: { key: RangeKey; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
  { key: "all", label: "All" },
];

export default function WorkAssignmentsDashboardCard() {
  const { user } = useProfile();
  const [rangeKey, setRangeKey] = useState<RangeKey>("week");

  const requestParams = useMemo(() => {
    const { from, to } = getRange(rangeKey);
    return {
      toUserId: user?.id ?? 0,
      status: "ASSIGNED",
      ...(from ? { plannedStartFrom: from } : {}),
      ...(to ? { plannedStartTo: to } : {}),
    };
  }, [user?.id, rangeKey]);

  const columns: ColumnDef<WorkAssignment>[] = useMemo(
    () => [
      // {
      //   header: "Assignee",
      //   id: "assignee",
      //   enableColumnFilter: false,
      //   cell: ({ row }) => (
      //     <Link to={assigneeLink(row.original)} className="fw-medium link">
      //       {assigneeName(row.original)}
      //     </Link>
      //   ),
      // },
      {
        header: "Source",
        id: "source",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const a = row.original;
          const cell = resolveMappedSourceCell(a.sourceType, a.sourceId, {
            typeLabelMap: WorkAssignmentSourceTypeLabel,
            source: a.source as { code?: string | null } | null,
          });
          const title = (a.source as any)?.title?.trim() || null;
          return (
            <div className="min-w-0">
              <SourceColumnContent value={cell} />
              {title ? (
                <div
                  className="text-muted small mt-1 text-truncate"
                  style={{ maxWidth: "13rem" }}
                >
                  {title}
                </div>
              ) : null}
            </div>
          );
        },
      },
      {
        header: "Planned start",
        id: "plannedStartDate",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const rel = relativePlannedStart(row.original.plannedStartDate);
          return rel ? (
            <span className={`small ${rel.cls}`}>{rel.label}</span>
          ) : (
            <span className="text-muted small">—</span>
          );
        },
      },
      {
        header: "Status",
        id: "status",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.status ? (
            <StatusBadge
              value={row.original.status}
              map={workAssignmentStatusMap}
            />
          ) : (
            <span className="text-muted">—</span>
          ),
      },
    ],
    [],
  );

  return (
    <Card className="mb-4">
      <CardHeader className="border-0 mb-1">
        <div className="d-flex align-items-center justify-content-between">
          <h5 className="card-title mb-0">My upcoming assignments</h5>
          <UncontrolledDropdown>
            <DropdownToggle tag="a" className="text-reset dropdown-btn" role="button">
              <span className="text-muted small me-1">
                {RANGE_OPTIONS.find((o) => o.key === rangeKey)?.label}
              </span>
              <i className="mdi mdi-dots-vertical align-middle text-muted fs-18" />
            </DropdownToggle>
            <DropdownMenu end>
              {RANGE_OPTIONS.map((opt) => (
                <DropdownItem key={opt.key} active={rangeKey === opt.key} onClick={() => setRangeKey(opt.key)}>
                  {opt.label}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
      </CardHeader>
      <CardBody>
        <TableContainer
          columns={columns}
          fetchApi={getWorkAssignments}
          requestParams={requestParams}
          syncPaginationToUrl={false}
          pageSize={10}
          globalSearch={false}
          hidePageSize
          divClass="table-responsive table-card mb-0"
          tableClass="table-nowrap align-middle mb-0"
          theadClass="table-light text-muted"
        />
      </CardBody>
    </Card>
  );
}
